import { Flex } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { OFText } from "design_system/components/OFText";
import { ClockIcon } from "design_system/icons";
import { EvalIds, RegionType } from "graphql/generated";
import { colors } from "src/colors";
import { CONSULT_ONLY_TREAMENTS } from "src/constants/Evaluations";
import { getRandomReviewContent } from "src/constants/Review.constant";
import { TRACKING_EVENTS_2 } from "src/dataTracking/events";
import { track$ } from "src/dataTracking/segment";
import { useConfig } from "src/hooks";
import { patientState, platformState } from "src/states";
import { checkCountry } from "src/utils/checkCountry";

import { StartedCardProps } from "./GetStartedCard";

type CardTextType = {
  title: string;
  subTitle?: string;
  description: string;
};

type Props = {
  onClick(): void;
  evaluationId: string;
};
const useGetStarted = ({ onClick, evaluationId }: Props) => {
  const { addSuffixCountry } = useConfig();
  const { t } = useTranslation([addSuffixCountry("evaluation")]);
  const router = useRouter();

  const patientData = useRecoilValue(patientState);
  const platform = useRecoilValue(platformState);
  const reviews = getRandomReviewContent(platform);

  const isConsultOnly = useMemo(
    () => CONSULT_ONLY_TREAMENTS.includes(evaluationId as EvalIds),
    [evaluationId]
  );

  const cardTexts: CardTextType[] = useMemo(
    () => t("get-started.card", { returnObjects: true }),
    [t]
  );

  const completeHealthProfileSubTitle = useMemo(
    () => (
      <Flex flexDirection="row" alignItems="center" marginTop={["12px", "16px"]}>
        <ClockIcon color={colors.white.default} size={24} />
        <OFText
          textTransform="uppercase"
          textStyle="subhead1"
          color={colors.white.default}
          marginLeft={1}
        >
          {cardTexts[0]?.subTitle}
        </OFText>
      </Flex>
    ),
    [cardTexts]
  );

  const getCardImageUrl = (index: number, country?: RegionType) => {
    const suffixCountry = checkCountry([country]) ? `-${country}` : "";

    return `/assets/evaluation/get-started-${index}${suffixCountry}.svg`;
  };

  const cardData: StartedCardProps[] = useMemo(() => {
    const cardList: StartedCardProps[] = cardTexts?.reduce((prev, curText, cardTextIndex) => {
      const curCardIndex = prev.length + 1;

      // consult only not have delivery step
      if (cardTextIndex === 2 && isConsultOnly) {
        return prev;
      }

      const subTitle = cardTextIndex === 0 ? completeHealthProfileSubTitle : curText.subTitle;
      const srcImage = getCardImageUrl(cardTextIndex + 1, cardTextIndex === 2 && RegionType.Jp);

      const curStartedCard: StartedCardProps = {
        index: curCardIndex,
        title: curText.title,
        description: curText.description,
        subTitle,
        srcImage
      };

      return [...prev, curStartedCard];
    }, []);

    cardList[0].highlight = true;
    cardList[cardList.length - 1].showDashLine = false;

    return cardList;
  }, [cardTexts, isConsultOnly, completeHealthProfileSubTitle]);

  const start = useCallback(async () => {
    onClick();
    track$({
      userId: patientData?.user?.id,
      event: TRACKING_EVENTS_2.evaluationStarted,
      properties: {
        platform,
        evaluation_type: evaluationId
      }
    });
  }, [evaluationId, onClick, patientData, platform]);

  const back = useCallback(async () => {
    router.back();
  }, [router]);

  return {
    reviews,
    cardData,
    t,
    back,
    start
  };
};

export default useGetStarted;
